import React, { useState } from 'react';
import { Calculator, ExternalLink, Pencil } from 'lucide-react';
import SportsbookLogo, { SPORTSBOOK_NAMES, SPORTSBOOK_URLS } from './SportsbookLogo';
import TeamLogo from './TeamLogo';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

function oddsToDecimal(american) {
  if (!american) return 2;
  if (american > 0) return american / 100 + 1;
  return 100 / Math.abs(american) + 1;
}


function formatOdds(odds) {
  return odds > 0 ? `+${odds}` : `${odds}`;
}

// Sport icons based on sport_key prefix
function getSportIcon(sportKey) {
  if (!sportKey) return '🏆';
  if (sportKey.includes('football')) return '🏈';
  if (sportKey.includes('basketball')) return '🏀';
  if (sportKey.includes('hockey')) return '🏒';
  if (sportKey.includes('soccer')) return '⚽';
  if (sportKey.includes('tennis')) return '🎾';
  if (sportKey.includes('mma')) return '🥊';
  if (sportKey.includes('baseball')) return '⚾';
  if (sportKey.includes('boxing')) return '🥊';
  return '🏆';
}

// Get market display name
function getMarketName(market) {
  if (!market || market === 'h2h') return 'Moneyline';
  if (market === 'spreads') return 'Spread';
  if (market === 'totals') return 'Total';
  if (market === 'player_props') return 'Prop';
  return market;
}

function formatPeriod(period, sportKey) {
  if (!period) return '';
  if (sportKey?.includes('basketball')) return `Q${period}`;
  if (sportKey?.includes('hockey')) return `P${period}`;
  if (sportKey?.includes('baseball')) {
    const sfx = ['st','nd','rd'][period-1] || 'th';
    return `${period}${sfx} Inn`;
  }
  if (sportKey?.includes('football')) return `Q${period}`;
  if (sportKey?.includes('tennis')) return `Set ${period}`;
  return `P${period}`;
}

// Get bet description based on market type
function getBetDescription(opp) {
  const market = opp.market || 'h2h';

  if (market === 'player_props') {
    // Prop arb format: "Player Name Stat Line"
    const propDisplay = opp.prop_display || opp.prop_type || '';
    return `${opp.player} ${propDisplay} ${opp.line}`;
  } else if (market === 'h2h') {
    return `${opp.home_team} vs ${opp.away_team}`;
  } else if (market === 'spreads') {
    return `${opp.home_team} vs ${opp.away_team}`;
  } else if (market === 'totals') {
    const point = opp.home_point ?? opp.away_point ?? opp.point ?? '';
    const clean = (n) => (n || '').replace(/\s*\([^)]+\)\s*$/, '').trim();
    const home = clean(opp.actual_home_team || opp.home_team);
    const away = clean(opp.actual_away_team || opp.away_team);
    return `${home} vs ${away} · O/U ${point}`;
  }
  return opp.event;
}

const stripSpread = (name) => (name || '').replace(/\s*\([^)]+\)\s*$/, '').trim();

function ArbitrageCardMini({ opportunity, onViewGame, onCalculate, session }) {
  const opp = opportunity;
  const isProp = opp.market === 'player_props';
  const isThreeWay = !!(opp.is_three_way || opp.has_draw);
  const [tracked, setTracked] = useState(false);
  const [trackingMode, setTrackingMode] = useState(false);
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');
  const [amountC, setAmountC] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedOdds1, setEditedOdds1] = useState('');
  const [editedOdds2, setEditedOdds2] = useState('');

  const profitLevel = opp.profit_percentage >= 2 ? 'high' :
                      opp.profit_percentage >= 1 ? 'medium' : 'low';

  const sportIcon = getSportIcon(opp.sport_key);
  const marketName = getMarketName(opp.market);
  const betDescription = getBetDescription(opp);

  // Handle prop arb vs game arb field names
  const side1BookKey = isProp ? opp.over_sportsbook_key : opp.home_sportsbook_key;
  const side1BookName = isProp ? opp.over_sportsbook : opp.home_sportsbook;
  const side1Odds = isProp ? opp.over_odds : opp.home_odds;

  const side2BookKey = isProp ? opp.under_sportsbook_key : opp.away_sportsbook_key;
  const side2BookName = isProp ? opp.under_sportsbook : opp.away_sportsbook;
  const side2Odds = isProp ? opp.under_odds : opp.away_odds;

  // Always show a descriptive label on each book's odds box
  const market = opp.market || 'h2h';
  const totalPoint = opp.home_point ?? opp.away_point ?? opp.point ?? '';
  const side1Label = isProp
    ? `Over ${opp.line ?? ''}`
    : market === 'totals'
    ? `Over ${totalPoint}`
    : market === 'spreads'
    ? (opp.home_team || '')
    : `${(opp.home_team || '').split(' ').pop()} ML`;

  const side2Label = isProp
    ? `Under ${opp.line ?? ''}`
    : market === 'totals'
    ? `Under ${totalPoint}`
    : market === 'spreads'
    ? (opp.away_team || '')
    : `${(opp.away_team || '').split(' ').pop()} ML`;

  // Draw side (3-way soccer arbs only)
  const side3BookKey = opp.draw_sportsbook_key;
  const side3BookName = opp.draw_sportsbook;
  const side3Odds = opp.draw_odds;

  const parsedEdit1 = parseInt(editedOdds1);
  const parsedEdit2 = parseInt(editedOdds2);
  const activeOdds1 = editMode && !isNaN(parsedEdit1) && parsedEdit1 !== 0 ? parsedEdit1 : side1Odds;
  const activeOdds2 = editMode && !isNaN(parsedEdit2) && parsedEdit2 !== 0 ? parsedEdit2 : side2Odds;

  let editedIsArb = null;
  let editedProfitPct = null;
  if (editMode) {
    const d1 = oddsToDecimal(activeOdds1);
    const d2 = oddsToDecimal(activeOdds2);
    const impl = 1/d1 + 1/d2 + (isThreeWay ? 1/oddsToDecimal(side3Odds) : 0);
    editedIsArb = impl < 1;
    editedProfitPct = editedIsArb ? (1 - impl) / impl * 100 : null;
  }

  async function handleTrack(e) {
    e.stopPropagation();
    if (!session) return;
    // For spreads arb, home_team/away_team include the spread annotation — strip it for raw names
    const stripSpreadAnnotation = (name) => name ? name.replace(/\s*\([+-]?\d+\.?\d*\)\s*$/, '').trim() : name;
    const rawHomeTeam = opp.market === 'spreads' ? stripSpreadAnnotation(opp.home_team) : opp.home_team;
    const rawAwayTeam = opp.market === 'spreads' ? stripSpreadAnnotation(opp.away_team) : opp.away_team;
    // Determine raw sides and points for auto-resolve
    const sideA = opp.market === 'totals' ? 'Over'
      : opp.market === 'spreads' ? rawHomeTeam
      : isProp ? 'Over'
      : opp.home_team;
    const sideB = opp.market === 'totals' ? 'Under'
      : opp.market === 'spreads' ? rawAwayTeam
      : isProp ? 'Under'
      : opp.away_team;
    // point_a and point_b in home-team perspective for the resolver
    const pointA = opp.market === 'spreads' ? opp.home_point
      : opp.market === 'totals' ? opp.point
      : null;
    const pointB = opp.market === 'spreads' ? (opp.away_point != null ? -opp.away_point : pointA)
      : opp.market === 'totals' ? opp.point
      : null;
    try {
      const notesPayload = {
        event_id: opp.event_id,
        sport_key: opp.sport_key,
        commence_time: opp.commence_time,
        home_team: rawHomeTeam,
        away_team: rawAwayTeam,
        side_a: sideA,
        side_b: sideB,
        point_a: pointA ?? null,
        point_b: pointB ?? null,
      };
      if (isProp) {
        notesPayload.player_name = opp.player;
        notesPayload.prop_type = opp.prop_type;
        notesPayload.prop_line = opp.line;
      }
      if (isThreeWay) {
        notesPayload.is_three_way = true;
        notesPayload.side_c = 'Draw';
        notesPayload.odds_c = side3Odds;
        notesPayload.amount_c = parseFloat(amountC) || 0;
        notesPayload.sportsbook_c = side3BookKey;
      }
      await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'arb',
          sportsbook: side1BookKey,
          eventName: opp.event,
          sportKey: opp.sport_key,
          market: opp.market,
          side: isProp ? `${opp.player} Over ${opp.line}${opp.prop_display ? ` (${opp.prop_display})` : ''}` : opp.home_team,
          odds: activeOdds1,
          amount: parseFloat(amountA) || 0,
          sportsbookB: side2BookKey,
          sideB: isProp ? `${opp.player} Under ${opp.line}${opp.prop_display ? ` (${opp.prop_display})` : ''}` : opp.away_team,
          oddsB: activeOdds2,
          amountB: parseFloat(amountB) || 0,
          notes: JSON.stringify(notesPayload),
        }),
      });
      setTrackingMode(false);
      setTracked(true);
    } catch (err) {
      console.error('Track failed:', err);
    }
  }

  const aA = parseFloat(amountA) || 0;
  const aB = parseFloat(amountB) || 0;
  const aC = parseFloat(amountC) || 0;
  const profitIfAWins = aA * (oddsToDecimal(activeOdds1) - 1) - aB - (isThreeWay ? aC : 0);
  const profitIfBWins = aB * (oddsToDecimal(activeOdds2) - 1) - aA - (isThreeWay ? aC : 0);
  const profitIfDrawWins = isThreeWay ? aC * (oddsToDecimal(side3Odds) - 1) - aA - aB : 0;
  const showPreview = aA > 0 || aB > 0 || (isThreeWay && aC > 0);
  const labelA = isProp ? `${opp.player} Over` : (opp.home_team || '').split(' ').pop();
  const labelB = isProp ? `${opp.player} Under` : (opp.away_team || '').split(' ').pop();

  return (
    <div className={`arb-card-mini profit-${profitLevel}${isThreeWay ? ' arb-card-mini--three-way' : ''}`} onClick={() => onViewGame && onViewGame(opp.event_id, opp.sport_key)}>
      {/* Header: Sport + Market + Live status */}
      <div className="arb-mini-header">
        <span className="arb-mini-sport">
          <span className="sport-emoji">{sportIcon}</span>
          {opp.sport_title || 'Sports'}
        </span>
        <div className="arb-mini-badges">
          {opp.is_live && opp.in_break && (
            <span className="live-break-badge">LIVE - Break</span>
          )}
          {opp.is_live && !opp.in_break && (
            <span className="live-badge">LIVE</span>
          )}
          <span className="arb-mini-market-badge">{marketName}</span>
        </div>
      </div>

      {/* Game name */}
      <div className="arb-mini-game">
        <span className="mini-game-team"><TeamLogo teamName={stripSpread(opp.actual_home_team || opp.home_team)} sportKey={opp.sport_key} size={13} />{opp.actual_home_team || opp.home_team}</span>
        <span className="mini-game-sep">vs</span>
        <span className="mini-game-team"><TeamLogo teamName={stripSpread(opp.actual_away_team || opp.away_team)} sportKey={opp.sport_key} size={13} />{opp.actual_away_team || opp.away_team}</span>
      </div>
      {!opp.is_live && opp.commence_time && (
        <div className="pc-game-time">
          {new Date(opp.commence_time).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
          })}
        </div>
      )}

      {/* Live score */}
      {opp.is_live && opp.home_score != null && (
        <div className="mini-card-scoreboard">
          {(() => {
            const isTennis = opp.sport_key?.includes('tennis');
            const homeName = ((opp.actual_home_team || opp.home_team) || '').split(' ').pop();
            const awayName = ((opp.actual_away_team || opp.away_team) || '').split(' ').pop();
            const [homeGames, awayGames] = isTennis && opp.clock ? opp.clock.split('-').map(Number) : [null, null];
            return isTennis ? (
              <table className="mini-score-tennis">
                <thead>
                  <tr>
                    <th />
                    <th className="mini-score-col-label">Sets</th>
                    <th className="mini-score-col-label">Games</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="mini-score-name">{homeName}</td>
                    <td className="mini-score-val"><strong>{opp.home_score}</strong></td>
                    <td className="mini-score-val">{homeGames ?? '-'}</td>
                  </tr>
                  <tr>
                    <td className="mini-score-name">{awayName}</td>
                    <td className="mini-score-val"><strong>{opp.away_score ?? '-'}</strong></td>
                    <td className="mini-score-val">{awayGames ?? '-'}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="mini-score-teams">
                <span>{homeName} <strong>{opp.home_score}</strong></span>
                <span className="score-sep">·</span>
                <span>{awayName} <strong>{opp.away_score ?? '-'}</strong></span>
              </div>
            );
          })()}
          {opp.period != null && (
            <span className="mini-score-period">
              {formatPeriod(opp.period, opp.sport_key)}{!opp.sport_key?.includes('tennis') && opp.clock ? ` ${opp.clock}` : ''}
            </span>
          )}
        </div>
      )}

      {/* Profit percentage - hero metric */}
      <div className="arb-mini-profit">
        {editMode ? (
          editedIsArb
            ? <span className="profit-value">{editedProfitPct.toFixed(2)}%</span>
            : <span className="edit-no-arb-warn">⚠ Not an arb</span>
        ) : (
          <span className="profit-value">{opp.profit_percentage}%</span>
        )}
        <span className="profit-label">PROFIT</span>
      </div>

      {/* Bet description */}
      <div className="arb-mini-bet-desc">
        <span className="bet-label">Bet:</span>
        <span className="bet-value">{betDescription}</span>
      </div>

      {/* Player info for props */}
      {isProp && opp.player_team && (
        <div className="ev-mini-player-info">
          {opp.player_jersey && <span className="ev-mini-player-jersey">#{opp.player_jersey}</span>}
          <TeamLogo teamName={opp.player_team} sportKey={opp.sport_key} size={13} />
          <span className="ev-mini-player-team">{opp.player_team}</span>
        </div>
      )}

      {/* Sportsbook sides with logos */}
      <div className={`arb-mini-sides${isThreeWay ? ' arb-mini-sides--three-way' : ''}`}>
        <div className="arb-mini-side">
          {side1Label && <span className="side-label">{side1Label}</span>}
          <div className="side-book-row">
            <SportsbookLogo bookKey={side1BookKey} size={18} />
            <span className="side-book-name">{SPORTSBOOK_NAMES[side1BookKey] || side1BookName}</span>
            {SPORTSBOOK_URLS[side1BookKey] && (
              <a
                href={SPORTSBOOK_URLS[side1BookKey]}
                target="_blank"
                rel="noopener noreferrer"
                className="book-link"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={12} />
              </a>
            )}
          </div>
          {editMode ? (
            <input
              className="odds-edit-input"
              value={editedOdds1}
              onChange={e => setEditedOdds1(e.target.value)}
              placeholder={String(side1Odds)}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="side-odds">{formatOdds(side1Odds)}</span>
          )}
        </div>
        {isThreeWay && (
          <>
            <span className="vs-divider">·</span>
            <div className="arb-mini-side">
              <span className="side-label">Draw</span>
              <div className="side-book-row">
                <SportsbookLogo bookKey={side3BookKey} size={18} />
                <span className="side-book-name">{SPORTSBOOK_NAMES[side3BookKey] || side3BookName}</span>
                {SPORTSBOOK_URLS[side3BookKey] && (
                  <a
                    href={SPORTSBOOK_URLS[side3BookKey]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="book-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
              <span className="side-odds">{formatOdds(side3Odds)}</span>
            </div>
          </>
        )}
        <span className="vs-divider">{isThreeWay ? '·' : 'vs'}</span>
        <div className="arb-mini-side">
          {side2Label && <span className="side-label">{side2Label}</span>}
          <div className="side-book-row">
            <SportsbookLogo bookKey={side2BookKey} size={18} />
            <span className="side-book-name">{SPORTSBOOK_NAMES[side2BookKey] || side2BookName}</span>
            {SPORTSBOOK_URLS[side2BookKey] && (
              <a
                href={SPORTSBOOK_URLS[side2BookKey]}
                target="_blank"
                rel="noopener noreferrer"
                className="book-link"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={12} />
              </a>
            )}
          </div>
          {editMode ? (
            <input
              className="odds-edit-input"
              value={editedOdds2}
              onChange={e => setEditedOdds2(e.target.value)}
              placeholder={String(side2Odds)}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="side-odds">{formatOdds(side2Odds)}</span>
          )}
        </div>
      </div>

      <div className="edit-odds-row">
        {!editMode && <span className="edit-odds-hint">Seeing different odds?</span>}
        <button
          className="edit-odds-btn"
          onClick={e => {
            e.stopPropagation();
            if (editMode) { setEditedOdds1(''); setEditedOdds2(''); }
            setEditMode(v => !v);
          }}
        >
          <Pencil size={11} />
          {editMode ? 'Reset odds' : 'Edit here'}
        </button>
      </div>
      <button
        className="arb-mini-calc-btn"
        onClick={(e) => {
          e.stopPropagation();
          if (!onCalculate) return;
          const oppToCalc = editMode
            ? {
                ...opp,
                [isProp ? 'over_odds' : 'home_odds']: activeOdds1,
                [isProp ? 'under_odds' : 'away_odds']: activeOdds2,
              }
            : opp;
          onCalculate(oppToCalc);
        }}
      >
        <Calculator size={14} />
        Calculate
      </button>

      {session && !tracked && !trackingMode && (
        <button
          className="ev-track-btn"
          onClick={(e) => { e.stopPropagation(); setAmountA(''); setAmountB(''); setAmountC(''); setTrackingMode(true); }}
        >
          Track Bet
        </button>
      )}
      {session && trackingMode && (
        <div className="arb-track-form" onClick={e => e.stopPropagation()}>
          <div className="arb-track-row">
            <span className="arb-track-label">{isThreeWay ? labelA : (SPORTSBOOK_NAMES[side1BookKey] || side1BookName)}</span>
            <input
              type="number"
              className="track-amount-input"
              value={amountA}
              onChange={e => setAmountA(e.target.value)}
              placeholder="Stake A ($)"
              min="0"
              step="1"
              autoFocus
            />
          </div>
          {isThreeWay && (
            <div className="arb-track-row">
              <span className="arb-track-label">Draw</span>
              <input
                type="number"
                className="track-amount-input"
                value={amountC}
                onChange={e => setAmountC(e.target.value)}
                placeholder="Stake Draw ($)"
                min="0"
                step="1"
              />
            </div>
          )}
          <div className="arb-track-row">
            <span className="arb-track-label">{isThreeWay ? labelB : (SPORTSBOOK_NAMES[side2BookKey] || side2BookName)}</span>
            <input
              type="number"
              className="track-amount-input"
              value={amountB}
              onChange={e => setAmountB(e.target.value)}
              placeholder="Stake B ($)"
              min="0"
              step="1"
            />
          </div>
          {showPreview && (
            <div className="arb-profit-preview">
              <div className="arb-profit-preview-row">
                <span>{labelA} wins:</span>
                <span className={profitIfAWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                  {profitIfAWins >= 0 ? '+' : ''}${profitIfAWins.toFixed(2)}
                </span>
              </div>
              {isThreeWay && (
                <div className="arb-profit-preview-row">
                  <span>Draw:</span>
                  <span className={profitIfDrawWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                    {profitIfDrawWins >= 0 ? '+' : ''}${profitIfDrawWins.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="arb-profit-preview-row">
                <span>{labelB} wins:</span>
                <span className={profitIfBWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                  {profitIfBWins >= 0 ? '+' : ''}${profitIfBWins.toFixed(2)}
                </span>
              </div>
            </div>
          )}
          <div className="track-amount-btns">
            <button className="track-amount-save" onClick={handleTrack}>Track</button>
            <button className="track-amount-cancel" onClick={(e) => { e.stopPropagation(); setTrackingMode(false); }}>✕</button>
          </div>
        </div>
      )}
      {session && tracked && (
        <button className="ev-track-btn ev-track-btn--done" onClick={e => e.stopPropagation()} disabled>Tracked ✓</button>
      )}
    </div>
  );
}

export default ArbitrageCardMini;
